import { extractAssistantTextFromJson } from "@/utils/meetingNotesChatSse";
import { sortMeetingChatHistoryChronologically } from "@/utils/meetingChatHistorySort";

function normSid(s: string): string {
  return s.trim().toLowerCase();
}

function rowSessionId(item: unknown): string | null {
  if (item == null || typeof item !== "object") return null;
  const v =
    (item as Record<string, unknown>).session_id ??
    (item as Record<string, unknown>).sessionId;
  if (typeof v === "string" && v.trim()) return v.trim();
  return null;
}

function historyRowsMatchSession(item: unknown, sessionId: string): boolean {
  const got = rowSessionId(item);
  if (!got) return false;
  return normSid(got) === normSid(sessionId);
}

export function normalizeMeetingChatHistoryEnvelope(
  data: unknown
): { history: unknown[] } {
  if (data == null) return { history: [] };
  if (Array.isArray(data)) return { history: data };
  if (typeof data !== "object") return { history: [] };
  const o = data as Record<string, unknown>;
  if (Array.isArray(o.history)) return { history: o.history };
  const nested = o.data;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    const dh = (nested as Record<string, unknown>).history;
    if (Array.isArray(dh)) return { history: dh };
  }
  return { history: [] };
}

function pushMeetingId(ordered: string[], seen: Set<string>, id: unknown): void {
  if (id == null) return;
  const t = String(id).trim();
  if (!t) return;
  const k = t.toLowerCase();
  if (seen.has(k)) return;
  seen.add(k);
  ordered.push(t);
}

function pushMeetingIdsFromRecord(
  ordered: string[],
  seen: Set<string>,
  rec: Record<string, unknown>
): void {
  const mids = rec.meeting_ids;
  if (Array.isArray(mids)) {
    for (const m of mids) pushMeetingId(ordered, seen, m);
  }
  pushMeetingId(ordered, seen, rec.meeting_id);
  pushMeetingId(ordered, seen, rec.meetingId);
}

/** Meeting ids from unified session history payload (root, interactions, nested envelopes). */
export function extractMeetingIdsFromSessionHistoryPayload(
  data: unknown,
  sessionId: string
): string[] {
  const ordered: string[] = [];
  const seen = new Set<string>();

  const { history } = normalizeMeetingChatHistoryEnvelope(data);
  for (const id of extractMeetingIdsFromHistoryForSession(history, sessionId)) {
    pushMeetingId(ordered, seen, id);
  }

  if (data == null || typeof data !== "object") return ordered;

  const root = data as Record<string, unknown>;
  pushMeetingIdsFromRecord(ordered, seen, root);

  const interactions = root.interactions;
  if (Array.isArray(interactions)) {
    for (const inter of interactions) {
      if (inter == null || typeof inter !== "object") continue;
      const rec = inter as Record<string, unknown>;
      pushMeetingIdsFromRecord(ordered, seen, rec);
      for (const key of ["response", "data", "metadata", "payload"] as const) {
        const nest = rec[key];
        if (nest && typeof nest === "object" && !Array.isArray(nest)) {
          pushMeetingIdsFromRecord(ordered, seen, nest as Record<string, unknown>);
        }
      }
    }
  }

  const nested = root.data;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    pushMeetingIdsFromRecord(ordered, seen, nested as Record<string, unknown>);
  }

  return ordered;
}

export function extractMeetingIdsFromHistoryForSession(
  history: unknown[],
  sessionId: string
): string[] {
  if (!Array.isArray(history) || !sessionId?.trim()) return [];

  const ordered: string[] = [];
  const seen = new Set<string>();

  const pushId = (id: string) => {
    const t = id.trim();
    if (!t) return;
    const k = t.toLowerCase();
    if (seen.has(k)) return;
    seen.add(k);
    ordered.push(t);
  };

  const pushFromRecord = (n: Record<string, unknown>) => {
    const mids = n.meeting_ids;
    if (Array.isArray(mids)) {
      for (const m of mids) {
        if (m != null && String(m).trim()) pushId(String(m));
      }
    }
    const mid = n.meeting_id;
    if (mid != null && String(mid).trim()) pushId(String(mid));
  };

  for (const item of history) {
    if (!historyRowsMatchSession(item, sessionId)) continue;
    if (item == null || typeof item !== "object") continue;
    const rec = item as Record<string, unknown>;
    pushFromRecord(rec);
    for (const key of ["response", "data", "metadata", "payload"] as const) {
      const nest = rec[key];
      if (nest && typeof nest === "object" && !Array.isArray(nest)) {
        pushFromRecord(nest as Record<string, unknown>);
      }
    }
  }
  return ordered;
}

export function filterAndSortMeetingHistoryForSession<T>(
  history: T[],
  sessionId: string
): T[] {
  if (!Array.isArray(history) || !sessionId?.trim()) return [];
  const filtered = history.filter((item) =>
    historyRowsMatchSession(item, sessionId)
  );
  return sortMeetingChatHistoryChronologically(filtered);
}

export function getMeetingHistoryMeetingName(item: unknown): string {
  if (item == null || typeof item !== "object") return "";
  const o = item as Record<string, unknown>;
  const v = o.meeting_name;
  if (typeof v === "string" && v.trim()) return v.trim();
  return "";
}

export function getMeetingHistoryUserQuery(item: unknown): string {
  if (item == null || typeof item !== "object") return "";
  const o = item as Record<string, unknown>;
  const keys = [
    "user_query",
    "query",
    "question",
    "user_message",
    "user_input",
    "input",
    "prompt",
  ] as const;
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

export function getMeetingHistoryAssistantText(item: unknown): string {
  if (item == null) return "";
  const direct = extractAssistantTextFromJson(item);
  if (direct.trim()) return direct.trim();
  if (typeof item !== "object") return "";
  const o = item as Record<string, unknown>;
  const response = o.response;
  if (response !== undefined) {
    const t = extractAssistantTextFromJson(response);
    if (t.trim()) return t.trim();
  }
  const answer = o.answer ?? o.output ?? o.assistant_message ?? o.bot_message;
  if (typeof answer === "string" && answer.trim()) return answer.trim();
  return "";
}
